/* An account's own Access view (the users button beside the settings gear):
   everyone who can work on this account and what they hold. Same grants and
   editor as Team — Team stays the place to manage a person across accounts
   (ADR 0019); this is the same data seen from one account. */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { Btn, ErrorLine, Icon, Loading, Spin } from '../ChatAssistant/compact/kit';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { AccessRow, Avatar, SearchInput } from '../Agency/team/AccessRow';
import { initial, nameOf } from '../Agency/team/people';
import { persistAccess } from '../Agency/team/persistAccess';
import { NO_ACCESS, fromAssignment, isEmpty, type Access } from '../Agency/team/permissionModel';
import '../Agency/team/team.css';

interface Member {
  user_id: string;
  email: string | null;
  username: string | null;
  access: Access;
  /** Holds access on the server (else: just added here, nothing granted yet). */
  held: boolean;
}

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;
/** Show the search box once the list is long enough to need it. */
const SEARCH_FROM = 6;

export function AccountAccessPanel({ accountId }: { accountId: string }) {
  const { t } = useTranslation('agency');
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [fresh, setFresh] = useState<string | null>(null); // just added: open its editor

  const load = useCallback(async () => {
    const rows = await api.getAccountMembers(accountId);
    setMembers(rows
      .map((r) => ({
        user_id: r.user_id, email: r.email, username: r.username ?? null, held: true,
        access: fromAssignment({ account_id: accountId, account_name: accountId, permissions: r.permissions, crm: r.crm }),
      }))
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b))));
  }, [accountId]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    load()
      .catch((e) => live && setError(errMsg(e, t('accountAccess.loadFailed'))))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [load, t]);

  const save = async (m: Member, next: Access) => {
    setSaving(m.user_id);
    setError(null);
    try {
      await persistAccess(null, m.user_id, accountId, next, m.held);
      setMembers((cur) => cur.map((x) => (x.user_id === m.user_id ? { ...x, access: next, held: !isEmpty(next) } : x)));
    } catch (e) {
      setError(errMsg(e, t('team.savePermissionsFailed')));
      await load().catch(() => {});
    }
    setSaving(null);
  };

  const remove = async (m: Member) => {
    if (m.held && !confirm(t('accountAccess.confirmRemove', { name: nameOf(m) }))) return;
    if (m.held) await save(m, NO_ACCESS);
    setMembers((cur) => cur.filter((x) => x.user_id !== m.user_id));
  };

  const add = async () => {
    const v = draft.trim();
    if (!v || adding) return;
    setAdding(true);
    setError(null);
    try {
      const r = await api.addGlobalTeamMember(v);
      setMembers((cur) => cur.some((x) => x.user_id === r.user_id)
        ? cur
        : [{ user_id: r.user_id, email: r.email, username: r.username ?? null, access: NO_ACCESS, held: false }, ...cur]);
      setFresh(r.user_id);
      setQuery('');
      setDraft('');
    } catch (e) {
      setError(errMsg(e, t('team.addTeamMemberFailed')));
    }
    setAdding(false);
  };

  const needle = query.trim().toLowerCase();
  const shown = members.filter((m) => !needle || [m.username, m.email].some((s) => s?.toLowerCase().includes(needle)));

  return (
    <div className="cpt-root tm-root" style={{ marginTop: 16 }}>
      <div className="tm-frame" style={{ minHeight: 0 }}>
        <div className="tm-head">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ font: f(700, 17, 1.2), color: C.text }}>{t('accountAccess.title')}</span>
            <span style={{ font: f(400, 12), color: C.sub }}>{t('accountAccess.subtitle')}</span>
          </div>
          <Btn kind="ghost" icon="users" onClick={() => navigate('/team')}>{t('accountAccess.openTeam')}</Btn>
        </div>

        {error && <ErrorLine>{error}</ErrorLine>}

        <div className="tm-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="tm-toolbar" style={{ flexWrap: 'wrap' }}>
            <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
              placeholder={t('team.addPlaceholder')} title={t('team.addHint')} className="tm-input" style={{ height: 30, flex: '1 1 220px', maxWidth: 340 }} />
            <Btn kind="primary" icon={adding ? undefined : 'user-plus'} onClick={() => void add()} disabled={!draft.trim() || adding}>
              {adding ? <Spin size={11} /> : t('accountAccess.add')}
            </Btn>
            <div style={{ flex: 1 }} />
            {members.length >= SEARCH_FROM && (
              <SearchInput value={query} onChange={setQuery} placeholder={t('fleet.searchPeople')} style={{ flex: '0 1 240px' }} />
            )}
          </div>

          {loading ? <Loading /> : (
            <>
              {!members.length && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 14px', font: f(400, 12.5), color: C.muted }}>
                  <Icon name="users" size={14} />{t('accountAccess.empty')}
                </div>
              )}
              {members.length > 0 && !shown.length && (
                <span style={{ padding: '14px', font: f(400, 12), color: C.muted }}>{t('fleet.noMatches', { q: query.trim() })}</span>
              )}
              {shown.map((m) => (
                <AccessRow
                  key={m.user_id}
                  defaultOpen={fresh === m.user_id}
                  avatar={<Avatar text={initial(nameOf(m))} size={28} round />}
                  title={<>{nameOf(m)}{m.username && m.email && <span style={{ color: C.muted, fontWeight: 400 }}> · {m.email}</span>}</>}
                  sub={m.held ? undefined : t('accountAccess.notGranted')}
                  access={m.access}
                  saving={saving === m.user_id}
                  onChange={(next) => void save(m, next)}
                  extra={(
                    <button type="button" title={t('accountAccess.remove')} aria-label={t('accountAccess.remove')}
                      onClick={() => void remove(m)} className="cpt-hover-icon tm-x">
                      <Icon name="x" size={13} />
                    </button>
                  )}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
